/**
 * Subscription Widget Functionality
 * Responsible for updating prices based on variant change and handling selling plan selection.
 */
(function() {
  const widgetId = 'js-subscription-widget';
  
  class SubscriptionWidget {
    constructor(container) {
      this.container = container;
      this.sectionId = container.dataset.sectionId;
      this.productId = container.dataset.productId;
      this.form = container.closest('form') || document.querySelector(`form[data-section-id="${this.sectionId}"]`);
      
      this.cards = container.querySelectorAll('.js-subscription-card');
      this.radios = container.querySelectorAll('.js-subscription-radio');
      this.sellingPlanInput = container.querySelector('#js-selling-plan-hidden-input');
      this.atcButton = this.form ? this.form.querySelector('.add-to-cart') : null;
      this.variantData = JSON.parse(document.getElementById(`VariantAllocations-${this.sectionId}`).textContent);
      
      // Find summary widget related to this form/section (sibling or in same parent)
      this.summaryWidget = container.nextElementSibling;
      if (this.summaryWidget && !this.summaryWidget.classList.contains('js-summary-widget')) {
        this.summaryWidget = container.parentNode.querySelector('.js-summary-widget');
      }
      if (!this.summaryWidget) {
         this.summaryWidget = document.querySelector('.js-summary-widget');
      }

      if (this.summaryWidget) {
        this.todayTotalEl = this.summaryWidget.querySelector('.js-today-total');
        this.recurringFiltersEl = this.summaryWidget.querySelector('.js-recurring-filters');
      }

      this.init();
    }

    init() {
      if (!this.container) return;

      // Event Listeners
      this.cards.forEach(card => {
        card.addEventListener('click', (e) => this.handleCardClick(card, e));
      });

      this.container.querySelectorAll('.js-selling-plan-select').forEach(select => {
        select.addEventListener('change', (e) => {
          this.updateSellingPlanId();
          this.onFrequencyChange(select);
        });
      });

      // Listen for variant changes from the main form
      if (this.form) {
        this.form.addEventListener('change', (e) => {
          if (e.target.name === 'id' || e.target.name.startsWith('option')) {
            // Delay to allow theme JS to update the variant ID
            setTimeout(() => this.onVariantChange(), 100);
          }
        });
        
        // mutation observer as fallback for variant change
        const idInput = this.form.querySelector('[name="id"]');
        if (idInput) {
          const observer = new MutationObserver(() => this.onVariantChange());
          observer.observe(idInput, { attributes: true, attributeFilter: ['value'] });
        }
      }

      // Initial state after a small delay to ensure theme JS is ready
      setTimeout(() => {
        this.onVariantChange();
        
        // select first available subscription by default if not already selected
        const firstSubCard = this.container.querySelector('.js-subscription-card[data-type="subscription"]');
        const anySelected = this.container.querySelector('.js-subscription-card.is-selected');
        
        if (firstSubCard && !anySelected) {
          this.handleCardClick(firstSubCard);
        } else if (anySelected) {
          this.updateSellingPlanId();
        }
      }, 300);
    }

    onVariantChange() {
      const variantIdInput = this.form ? this.form.querySelector('[name="id"]') : null;
      if (!variantIdInput) return;

      const variantId = variantIdInput.value;
      const data = this.variantData[variantId];
      if (!data) return;

      this.filterCards(data);
      this.updateCardPrices(data);
      this.updateSellingPlanId();
    }

    filterCards(variantData) {
      let firstVisibleCard = null;
      let firstVisibleSubscriptionCard = null;
      let currentlySelectedVisible = false;

      this.cards.forEach(card => {
        const type = card.dataset.type;
        const groupId = card.dataset.groupId;
        let isVisible = true;

        if (type === 'subscription') {
          // Visible if current variant has allocations for this group
          isVisible = !!(variantData.allocations && variantData.allocations[groupId]);
          
          if (isVisible) {
            // Further filter the selling plan options within this group
            this.updateSellingPlanOptions(card, variantData.allocations[groupId].selling_plans);
            if (!firstVisibleSubscriptionCard) firstVisibleSubscriptionCard = card;
          }
        } else if (type === 'one-time') {
          // Visible if variant doesn't require a selling plan
          isVisible = !variantData.requires_selling_plan;
        }

        if (isVisible) {
          card.style.display = 'flex';
          if (!firstVisibleCard) firstVisibleCard = card;
          if (card.classList.contains('is-selected')) currentlySelectedVisible = true;
        } else {
          card.style.display = 'none';
          card.classList.remove('is-selected');
        }
      });

      // By default, select the subscription plan whenever the variant changes
      if (firstVisibleSubscriptionCard) {
        this.handleCardClick(firstVisibleSubscriptionCard);
      } else if (firstVisibleCard) {
        this.handleCardClick(firstVisibleCard);
      }
    }

    updateSellingPlanOptions(card, availablePlans) {
      const select = card.querySelector('.js-selling-plan-select');
      if (!select || select.tagName !== 'SELECT') return;

      const availableIds = availablePlans.map(p => p.id.toString());
      let visibleOptionsCount = 0;
      let firstVisibleOption = null;

      Array.from(select.options).forEach(option => {
        const planId = option.dataset.planId;
        if (availableIds.includes(planId)) {
          option.style.display = '';
          option.disabled = false;
          visibleOptionsCount++;
          if (!firstVisibleOption) firstVisibleOption = option;
        } else {
          option.style.display = 'none';
          option.disabled = true;
        }
      });

      // If the current value is now hidden, select the first available one
      if (select.value && !availableIds.includes(select.value)) {
        if (firstVisibleOption) {
          select.value = firstVisibleOption.value;
        }
      }
      
      // Hide the entire frequency selector if only 1 option is available
      const frequencyWrapper = card.querySelector('.subscription-card__frequency');
      if (frequencyWrapper) {
         frequencyWrapper.style.display = visibleOptionsCount > 1 ? 'block' : 'none';
      }
    }

    onFrequencyChange(select) {
      const card = select.closest('.js-subscription-card');
      if (!card || !card.classList.contains('is-selected')) return;
      
      // Update price display when frequency changes
      const variantIdInput = this.form ? this.form.querySelector('[name="id"]') : null;
      if (!variantIdInput) return;
      const data = this.variantData[variantIdInput.value];
      if (data) this.updateCardPrices(data);
    }

    updateCardPrices(variantData) {
      this.cards.forEach(card => {
        const type = card.dataset.type;
        const groupId = card.dataset.groupId;
        const priceElement = card.querySelector('.js-plan-price');
        const comparePriceElement = card.querySelector('.js-plan-compare-price');
        
        let price = variantData.price;
        let compareAtPrice = variantData.compare_at_price;

        if (type === 'subscription' && variantData.allocations[groupId]) {
          const allocation = variantData.allocations[groupId];
          const select = card.querySelector('.js-selling-plan-select');
          let selectedPlanId = select ? select.value : null;
          
          let allocationPrice = allocation.price; // Default to first plan price
          
          if (selectedPlanId) {
            const specificPlan = allocation.selling_plans.find(p => p.id.toString() === selectedPlanId);
            if (specificPlan) allocationPrice = specificPlan.price;
          }

          // If subscription is cheaper than one-time, use one-time as compare price
          if (allocationPrice < variantData.price) {
            compareAtPrice = variantData.price;
          }
          price = allocationPrice;
        }

        if (priceElement) {
          priceElement.textContent = this.formatMoney(price);
        }

        if (comparePriceElement) {
          if (compareAtPrice > price) {
            comparePriceElement.textContent = this.formatMoney(compareAtPrice);
            comparePriceElement.style.display = 'inline';
          } else {
            comparePriceElement.style.display = 'none';
          }
        }
      });
    }

    handleCardClick(card, event) {
      // Prevent double triggers if clicking radio directly
      if (event && event.target.classList.contains('js-subscription-radio')) {
         // Radio change event will handle it
      }

      this.cards.forEach(c => c.classList.remove('is-selected'));
      card.classList.add('is-selected');
      
      const radio = card.querySelector('.js-subscription-radio');
      if (radio) radio.checked = true;

      this.updateSellingPlanId();
    }

    updateSellingPlanId() {
      const selectedCard = this.container.querySelector('.js-subscription-card.is-selected');
      if (!selectedCard) return;

      const type = selectedCard.dataset.type;
      let sellingPlanId = '';

      if (type === 'subscription') {
        const select = selectedCard.querySelector('.js-selling-plan-select');
        sellingPlanId = select.value;
      }

      this.sellingPlanInput.value = sellingPlanId;
      this.updateATCButton(selectedCard);
      this.updateSummaryWidget(selectedCard);
    }

    updateATCButton(selectedCard) {
      if (!this.atcButton) return;
      
      const priceTextElement = this.atcButton.querySelector('.js-atc-button-price');
      const textElement = this.atcButton.querySelector('.js-atc-button-text');
      const planPriceElement = selectedCard.querySelector('.js-plan-price');
      const type = selectedCard.dataset.type;

      if (priceTextElement && planPriceElement) {
        priceTextElement.textContent = `- ${planPriceElement.textContent}`;
      }
    }

    updateSummaryWidget(selectedCard) {
      if (!this.summaryWidget) return;

      const type = selectedCard.dataset.type;
      
      if (type === 'subscription') {
        this.summaryWidget.style.display = 'block';

        const variantIdInput = this.form ? this.form.querySelector('[name="id"]') : null;
        if (!variantIdInput) return;
        const variantId = variantIdInput.value;
        const data = this.variantData[variantId];
        if (!data) return;

        // Today's Total
        const planPriceElement = selectedCard.querySelector('.js-plan-price');
        if (this.todayTotalEl && planPriceElement) {
          this.todayTotalEl.textContent = planPriceElement.textContent;
        }

        // Recurring Filters Price
        if (this.recurringFiltersEl) {
           let recurringVal = data.recurring_filters;
           // attempt to parse if it's stored as an integer (cents) or "$10.00" string
           if (recurringVal != null && recurringVal !== "") {
             if (!isNaN(Number(recurringVal))) {
                this.recurringFiltersEl.textContent = this.formatMoney(Number(recurringVal));
             } else {
                this.recurringFiltersEl.textContent = recurringVal;
             }
             this.recurringFiltersEl.parentElement.style.display = 'flex';
           } else {
             this.recurringFiltersEl.parentElement.style.display = 'none';
           }
        }
      } else {
        this.summaryWidget.style.display = 'none';
      }
    }

    formatMoney(cents) {
      let formatted = "";
      if (window.Shopify && window.Shopify.formatMoney) {
        const format = document.querySelector('meta[name="shopify-money-format"]')?.content || "${{amount}}";
        formatted = window.Shopify.formatMoney(cents, format);
      } else {
        const currency = window.Shopify?.currency?.active || 'USD';
        formatted = (cents / 100).toLocaleString(undefined, { style: 'currency', currency: currency });
      }
      // Remove trailing .00
      return formatted.replace(/\.00$/, '');
    }
  }

  // Initialize all widgets on page load
  const initWidgets = () => {
    document.querySelectorAll('.js-subscription-widget').forEach(container => {
      new SubscriptionWidget(container);
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initWidgets);
  } else {
    initWidgets();
  }
})();
